import { useCallback, useEffect, useRef, useState } from 'react';

interface StuckSentinel {
  /** attach to a zero-height element placed immediately before the sticky bar */
  sentinelRef: (node: HTMLElement | null) => void;
  /** true once the sentinel has scrolled out of view, i.e. the bar is pinned */
  isStuck: boolean;
}

/**
 * Reports whether a `position: sticky` element has actually stuck.
 *
 * CSS has no "is it pinned" selector, so this watches a zero-height sentinel
 * sitting just above the sticky element: while the sentinel is on screen the
 * bar is in its normal place; once it scrolls past, the bar is pinned.
 *
 * Uses IntersectionObserver rather than a scroll listener so the check runs off
 * the main thread's scroll path. Where the API is missing the hook simply never
 * reports stuck, which degrades to the bar's full-size styling.
 */
const useStuckSentinel = (): StuckSentinel => {
  const [isStuck, setIsStuck] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => () => {
    observerRef.current?.disconnect();
    observerRef.current = null;
  }, []);

  const sentinelRef = useCallback((node: HTMLElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;

    if (!node || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(([entry]) => {
      setIsStuck(!entry.isIntersecting);
    });
    observer.observe(node);
    observerRef.current = observer;
  }, []);

  return { sentinelRef, isStuck };
};

export default useStuckSentinel;
